import React from 'react';
import {
  LayoutDashboard,
  BrainCircuit,
  FileText,
  Users,
  Printer,
  ShieldCheck,
  ChevronRight,
  HeartHandshake,
  Radio,
  ShieldAlert,
  HeartPulse
} from 'lucide-react';

export type NavTab = 'command' | 'ambientSafety' | 'dashboard' | 'remoteRPM' | 'seniorLiving' | 'wellness' | 'clinicalDocs' | 'patients' | 'printCenter';

interface SidebarProps {
  currentTab: NavTab;
  onSelectTab: (tab: NavTab) => void;
  unresolvedAlertCount: number;
  activeDocCount: number;
  turnsDueSoonCount?: number;
  activeSensorAlertCount?: number;
  activeAmbientAlertCount?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentTab,
  onSelectTab,
  unresolvedAlertCount,
  activeDocCount,
  turnsDueSoonCount = 1,
  activeSensorAlertCount = 2,
  activeAmbientAlertCount = 1
}) => {
  const navItems = [
    {
      id: 'command' as NavTab,
      label: 'Command Center',
      sublabel: 'Executive Ops & AI HUD',
      icon: BrainCircuit,
      badge: unresolvedAlertCount > 0 ? `${unresolvedAlertCount} Alerts` : 'Optimal',
      badgeColor: unresolvedAlertCount > 0 ? 'bg-rose-500/20 text-rose-300 border-rose-500/30' : 'bg-teal-500/10 text-teal-400 border-teal-500/20'
    },
    {
      id: 'ambientSafety' as NavTab,
      label: 'Ambient Safety & Radars',
      sublabel: 'Aqara FP2 • Rooms 101-103',
      icon: Radio,
      badge: activeAmbientAlertCount > 0 ? `${activeAmbientAlertCount} Fall Alert` : 'Aqara Live',
      badgeColor: activeAmbientAlertCount > 0 ? 'bg-rose-500/20 text-rose-300 border-rose-500/30 animate-pulse' : 'bg-teal-500/20 text-teal-300 border-teal-500/40'
    },
    {
      id: 'dashboard' as NavTab,
      label: 'Telemetry Dashboard',
      sublabel: 'Beds, Vitals & Flow',
      icon: LayoutDashboard,
      badge: 'Live',
      badgeColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
    },
    {
      id: 'remoteRPM' as NavTab,
      label: 'Remote Care & RPM',
      sublabel: 'Sensors & Home Telemetry',
      icon: Radio,
      badge: activeSensorAlertCount > 0 ? `${activeSensorAlertCount} Alerts` : 'CPT 99454',
      badgeColor: activeSensorAlertCount > 0 ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' : 'bg-slate-700/50 text-slate-300 border-slate-600/40'
    },
    {
      id: 'seniorLiving' as NavTab,
      label: 'Senior Living & Care',
      sublabel: 'ADLs, Turns & Family AI',
      icon: HeartHandshake,
      badge: turnsDueSoonCount > 0 ? `${turnsDueSoonCount} Turns Due` : 'All Good',
      badgeColor: turnsDueSoonCount > 0 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
    },
    {
      id: 'wellness' as NavTab,
      label: 'Preventive Wellness',
      sublabel: 'Longevity, Sleep & Fitness',
      icon: HeartPulse,
      badge: 'Google Health',
      badgeColor: 'bg-teal-500/20 text-teal-300 border-teal-500/30'
    },
    {
      id: 'clinicalDocs' as NavTab,
      label: 'ClinicalDocs AI',
      sublabel: 'SOAP Notes & ICD-10',
      icon: FileText,
      badge: `${activeDocCount} Ready`,
      badgeColor: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20'
    },
    {
      id: 'patients' as NavTab,
      label: 'Active Census',
      sublabel: 'Inpatient Registry',
      icon: Users,
      badge: '4 Active',
      badgeColor: 'bg-slate-700/50 text-slate-300 border-slate-600/40'
    },
    {
      id: 'printCenter' as NavTab,
      label: 'Print & Audit Center',
      sublabel: 'EHRs & Pocket Cards',
      icon: Printer,
      badge: 'Certified',
      badgeColor: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20'
    }
  ];

  return (
    <aside className="w-64 bg-slate-900/60 border-r border-slate-800 flex flex-col justify-between shrink-0 select-none">
      <div className="p-4 space-y-1">
        <div className="px-3 py-2 text-[10px] font-mono tracking-wider uppercase text-slate-400 flex items-center justify-between">
          <span>Operating Modules</span>
          <span className="h-1.5 w-1.5 rounded-full bg-teal-400"></span>
        </div>

        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentTab === item.id;

          return (
            <button
              key={item.id}
              onClick={() => onSelectTab(item.id)}
              className={`w-full text-left p-3 rounded-xl transition flex items-center justify-between group ${
                isActive
                  ? 'bg-gradient-to-r from-teal-500/15 to-transparent border border-teal-500/40 text-white shadow-sm shadow-teal-500/10'
                  : 'hover:bg-slate-800/60 text-slate-400 hover:text-slate-200 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg transition ${
                    isActive ? 'bg-teal-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-400 group-hover:text-teal-400'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-200">{item.label}</div>
                  <div className="text-[10px] text-slate-400">{item.sublabel}</div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <span
                  className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${item.badgeColor}`}
                >
                  {item.badge}
                </span>
                {isActive && <ChevronRight className="h-3 w-3 text-teal-400" />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Compliance / Accreditation Footer */}
      <div className="p-4 border-t border-slate-800 bg-slate-950/40">
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-3 text-xs space-y-2">
          <div className="flex items-center gap-2 text-teal-400 font-semibold text-[11px]">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>HIPAA & HL7 Compliant</span>
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            Encrypted with 256-bit AES ledger. Audit trails signed with SHA-256 digital seals.
          </p>
          <div className="flex items-center justify-between pt-1 text-[10px] font-mono text-slate-400 border-t border-slate-800/80">
            <span>Node: MC-US-EAST</span>
            <span className="text-emerald-400">99.99% Up</span>
          </div>
        </div>
      </div>
    </aside>
  );
};
